import React from 'react';
import {
  EndDateConfigKind,
  EndDateTimeUnitKind,
  IEndDateConfigInput,
} from '../../../../utils/apollo/graphqlSchema';
import {
  DaysNumberInput,
  EndDateDetailsRow,
  WideSingleSelectMenu,
} from './DataModelerDataSourceInputUI';
import { DataModelerConfigLabelWithInfo } from '../DataModelerShared';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import {
  FieldLabel,
  InputField,
  VerticalField,
} from '../../../../components/Form';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  readOnly: boolean;
  endDate: IEndDateConfigInput;
  onChange: (endDate: IEndDateConfigInput) => void;
}

const defaultEndDateConfig = (): IEndDateConfigInput => {
  return {
    kind: EndDateConfigKind.Current,
    rollingCount: null,
    rollingTimeUnit: null,
    startTimeUnit: null,
    staticDate: null,
    staticDateAsLiveStart: false,
  };
};
type END_DATE_CONFIG_UI_KINDS = 'Static' | 'Dynamic' | 'StartOfWeek';
const END_DATE_CONFIG_KINDS_OPTS: {
  text: string;
  value: END_DATE_CONFIG_UI_KINDS;
}[] = [
  { text: 'Static', value: 'Static' },
  { text: 'Dynamic', value: 'Dynamic' },
  { text: 'Start Of Week', value: 'StartOfWeek' },
];

const endDateConfigKindToUiType = (
  configKind: EndDateConfigKind | null,
): END_DATE_CONFIG_UI_KINDS => {
  switch (configKind) {
    case null:
      return 'Dynamic';
    case EndDateConfigKind.Current:
      return 'Dynamic';
    case EndDateConfigKind.Rolling:
      return 'Dynamic';
    case EndDateConfigKind.Start:
      return 'StartOfWeek';
    case EndDateConfigKind.Static:
      return 'Static';
    default:
      throw new UnreachableCaseError(configKind);
  }
};

const DataModelerEndDateInput = ({ readOnly, endDate, onChange }: IProps) => {
  const endDateKind = endDateConfigKindToUiType(endDate.kind);
  const rollingCount = endDate.rollingCount ?? 1;

  const setEndDateKind = (newType: string) => {
    const uiKind = newType as END_DATE_CONFIG_UI_KINDS;
    if (uiKind !== endDateKind) {
      switch (uiKind) {
        case 'Static':
          onChange({
            ...defaultEndDateConfig(),
            kind: EndDateConfigKind.Static,
          });
          break;
        case 'Dynamic':
          onChange({
            ...defaultEndDateConfig(),
            kind: EndDateConfigKind.Current,
          });
          break;
        case 'StartOfWeek':
          onChange({
            ...defaultEndDateConfig(),
            kind: EndDateConfigKind.Start,
            startTimeUnit: EndDateTimeUnitKind.Week,
          });
          break;
        default:
          throw new UnreachableCaseError(uiKind);
      }
    }
  };

  const setStaticDate = (newDateStr: string) => {
    onChange({
      ...endDate,
      staticDate: newDateStr,
    });
  };

  const setRollingDaysCount = (newRollingCount: number | null) => {
    if (newRollingCount === 1) {
      onChange(defaultEndDateConfig());
    } else {
      onChange({
        ...defaultEndDateConfig(),
        kind: EndDateConfigKind.Rolling,
        rollingCount: newRollingCount,
        rollingTimeUnit: EndDateTimeUnitKind.Day,
      });
    }
  };

  return (
    <>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'End Date Type'}
          tooltipOption={dataModelLabelInfoTooltipOptions.connectionEndDateType}
        />
        <WideSingleSelectMenu
          disabled={readOnly}
          value={endDateKind}
          onChange={setEndDateKind}
          options={END_DATE_CONFIG_KINDS_OPTS}
        />
      </VerticalField>
      {endDateKind === 'Static' && (
        <VerticalField noMargin={true}>
          <FieldLabel>Enter Static Date</FieldLabel>
          <InputField
            value={endDate.staticDate ?? ''}
            onChange={setStaticDate}
            disabled={readOnly}
            placeholder={'YYYY-MM-DD'}
          />
        </VerticalField>
      )}
      {endDateKind === 'Dynamic' && (
        <>
          <EndDateDetailsRow>
            Today Minus
            <DaysNumberInput
              type="number"
              value={rollingCount}
              onChange={setRollingDaysCount}
              disabled={readOnly}
            />
            Day(s)
          </EndDateDetailsRow>
        </>
      )}
    </>
  );
};

export default DataModelerEndDateInput;
